import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Clock3,
  Expand,
  LogOut,
  Menu,
  Moon,
  Settings,
  Sun,
  Trophy,
  ZoomIn,
  ZoomOut
} from "lucide-react";
import API from "../services/api";
import { getStoredAdmin } from "../utils/adminAuth";

function AdminTopbar({ isMobile = false, isLaptop = false, onMenuClick = () => {} }) {
  const navigate = useNavigate();
  const admin = getStoredAdmin();
  const [now, setNow] = useState(new Date());
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState({ deposits: [], withdraws: [] });
  const [seenNotice, setSeenNotice] = useState(() => readSeenNotice(admin));
  const [theme, setTheme] = useState(() => localStorage.getItem("admin_theme") || "light");
  const [zoom, setZoom] = useState(() => clampZoom(localStorage.getItem("admin_zoom") || "0.86"));
  const [showZoomTools, setShowZoomTools] = useState(false);
  const [displayName, setDisplayName] = useState(() => localStorage.getItem("admin_display_name") || admin?.name || "Admin");
  const [nameDraft, setNameDraft] = useState(() => localStorage.getItem("admin_display_name") || admin?.name || "Admin");
  const [showNameEditor, setShowNameEditor] = useState(false);
  const [activity, setActivity] = useState([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.adminTheme = theme;
    localStorage.setItem("admin_theme", theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.style.setProperty("--admin-ui-zoom", String(zoom));
    localStorage.setItem("admin_zoom", String(zoom));
  }, [zoom]);

  useEffect(() => {
    const total = getUnseenNoticeCount(notifications, seenNotice);
    document.title = total > 0 ? `(${total}) Matka Kings Admin` : "Matka Kings Admin";
  }, [notifications, seenNotice]);

  const timeLabel = useMemo(() => {
    return now.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).toLowerCase();
  }, [now]);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch {
      // ignore
    }
  };

  const saveDisplayName = () => {
    const clean = String(nameDraft || "").trim().slice(0, 32) || "Admin";
    setDisplayName(clean);
    setNameDraft(clean);
    localStorage.setItem("admin_display_name", clean);
    setShowNameEditor(false);
  };

  const openNoticePage = (path) => {
    markNotificationsSeen(notifications);
    setShowNotifications(false);
    navigate(path);
  };

  const loadNotifications = useCallback(async () => {
    try {
      const [depRes, wdRes] = await Promise.all([
        API.get("/admin/deposits"),
        API.get("/admin/withdraws")
      ]);

      const allDeposits = Array.isArray(depRes.data) ? depRes.data : [];
      const allWithdraws = Array.isArray(wdRes.data) ? wdRes.data : [];
      const approvedDeposits = allDeposits.filter((item) => String(item.status || "").toLowerCase() === "approved");
      const approvedWithdraws = allWithdraws.filter((item) => String(item.status || "").toLowerCase() === "approved");

      const sortByDate = (items) => items.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setNotifications({
        deposits: sortByDate(approvedDeposits).slice(0, 3),
        withdraws: sortByDate(approvedWithdraws).slice(0, 3),
      });
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = window.setInterval(loadNotifications, 15000);
    return () => window.clearInterval(interval);
  }, [loadNotifications]);

  const markNotificationsSeen = useCallback((source = notifications) => {
    const nextSeen = {
      depositIds: mergeSeenIds(seenNotice.depositIds, source.deposits),
      withdrawIds: mergeSeenIds(seenNotice.withdrawIds, source.withdraws),
      deposits: Math.max(Number(seenNotice.deposits || 0), getMaxId(source.deposits)),
      withdraws: Math.max(Number(seenNotice.withdraws || 0), getMaxId(source.withdraws)),
    };
    if (
      nextSeen.deposits === Number(seenNotice.deposits || 0)
      && nextSeen.withdraws === Number(seenNotice.withdraws || 0)
      && sameIdList(nextSeen.depositIds, seenNotice.depositIds)
      && sameIdList(nextSeen.withdrawIds, seenNotice.withdrawIds)
    ) {
      return;
    }
    setSeenNotice(nextSeen);
    localStorage.setItem(getSeenNoticeKey(admin), JSON.stringify(nextSeen));
  }, [admin, notifications, seenNotice]);

  useEffect(() => {
    if (showNotifications) markNotificationsSeen(notifications);
  }, [markNotificationsSeen, notifications, showNotifications]);

  useEffect(() => {
    let active = true;
    const loadActivity = () => {
      API.get("/admin/activity")
        .then((res) => {
          if (active) setActivity(Array.isArray(res.data) ? res.data : []);
        })
        .catch(() => {});
    };
    loadActivity();
    const interval = window.setInterval(loadActivity, 60000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, []);

  const totalNotice = getUnseenNoticeCount(notifications, seenNotice);
  const pendingDepositCount = getUnseenItems(notifications.deposits, seenNotice, "deposits").length;
  const pendingWithdrawCount = getUnseenItems(notifications.withdraws, seenNotice, "withdraws").length;
  const visibleNoticeCount = notifications.deposits.length + notifications.withdraws.length;
  const zoomLabel = `${Math.round(zoom * 100)}%`;
  const adminDisplayName = displayName || admin?.name || "Admin";

  const bestShift = activity[0];
  const activeTopbar = theme === "dark" ? darkTopbar(isMobile ? mobileTopbar : isLaptop ? laptopTopbar : topbar) : isMobile ? mobileTopbar : isLaptop ? laptopTopbar : topbar;

  return (
    <header style={activeTopbar} className="admin-topbar">
      <div style={isMobile ? mobileLeftCluster : isLaptop ? laptopLeftCluster : leftCluster}>
        {isMobile && (
          <button type="button" style={mobileMenuButton} onClick={onMenuClick}>
            <Menu size={18} />
          </button>
        )}
        <button type="button" style={isMobile ? mobileUserPill : isLaptop ? laptopUserPill : userPill} onClick={() => setShowNameEditor((value) => !value)}>
          <span style={avatar}>{String(adminDisplayName || "A").trim().charAt(0).toUpperCase()}</span>
          <span>
            <strong>{adminDisplayName}</strong>
            {!isMobile && <small>{admin?.role === "sub_admin" ? "Sub Admin" : "Live Admin"}</small>}
          </span>
        </button>
        {!isMobile && (
          <button type="button" title={bestShift ? `Rank #${bestShift.rank}` : "Admin shift ranking"} style={isLaptop ? laptopIconButton : iconButton} onClick={() => setShowLeaderboard(true)}>
            <Trophy size={14} />
          </button>
        )}
      </div>

      <div style={isMobile ? mobileRightCluster : isLaptop ? laptopRightCluster : rightCluster}>
        {!isMobile && <div style={isLaptop ? laptopTimePill : timePill}>
          <Clock3 size={11} />
          {timeLabel}
        </div>}
        <div style={toolWrap}>
          <button type="button" title={`Zoom ${zoomLabel}`} style={isMobile ? mobileZoomTrigger : isLaptop ? laptopZoomTrigger : zoomTrigger} onClick={() => setShowZoomTools((value) => !value)}>
            {zoomLabel}
          </button>
          {showZoomTools && (
            <div style={isMobile ? mobileZoomPanel : zoomPanel}>
              <button type="button" style={zoomToolButton} onClick={() => setZoom((value) => clampZoom(Number(value) - 0.05))}><ZoomOut size={14} /></button>
              <strong style={zoomValue}>{zoomLabel}</strong>
              <button type="button" style={zoomToolButton} onClick={() => setZoom((value) => clampZoom(Number(value) + 0.05))}><ZoomIn size={14} /></button>
            </div>
          )}
        </div>
        <button
          type="button"
          style={isMobile ? mobileIconButton : isLaptop ? laptopIconButton : iconButton}
          onClick={() => {
            setShowNotifications((current) => {
              const next = !current;
              if (next) markNotificationsSeen();
              return next;
            });
          }}
        >
          {totalNotice > 0 && <span style={noticeCount}>{totalNotice}</span>}
          <Bell size={13} />
        </button>
        {!isMobile && <button type="button" style={isLaptop ? laptopIconButton : iconButton} onClick={toggleFullscreen}><Expand size={13} /></button>}
        <button type="button" title="Toggle admin theme" style={isMobile ? mobileThemeButton : isLaptop ? laptopThemeButton : themeButton} onClick={() => setTheme((current) => current === "dark" ? "light" : "dark")}>
          {theme === "dark" ? <Sun size={13} /> : <Moon size={13} />}
          <span>{theme === "dark" ? "Light" : "Dark"}</span>
        </button>
        {!isMobile && <button type="button" style={isLaptop ? laptopIconButton : iconButton} onClick={() => navigate("/admin/settings")}><Settings size={13} /></button>}
        <button
          type="button"
          style={isMobile ? mobileLogoutButton : isLaptop ? laptopLogoutButton : logoutButton}
          onClick={() => {
            localStorage.removeItem("admin");
            navigate("/admin-login");
          }}
        >
          <LogOut size={13} />
        </button>
      </div>

      {showNameEditor && (
        <div style={isMobile ? mobileNameEditorPanel : nameEditorPanel}>
          <strong style={nameEditorTitle}>Admin Name</strong>
          <input
            style={nameEditorInput}
            value={nameDraft}
            onChange={(event) => setNameDraft(event.target.value)}
            placeholder="Enter display name"
            maxLength={32}
          />
          <button type="button" style={nameEditorSave} onClick={saveDisplayName}>Save</button>
        </div>
      )}

      {showNotifications && (
        <div style={isMobile ? mobileNotificationPanel : notificationPanel}>
          <div style={notificationHeader}>
            <div>
              <strong>Latest Approved D/W</strong>
              <div style={notificationSub}>{totalNotice ? `${pendingDepositCount} new deposit and ${pendingWithdrawCount} new payout entr${totalNotice === 1 ? "y" : "ies"}` : "No new D/W entries"}</div>
            </div>
            <button type="button" style={markReadButton} onClick={() => { markNotificationsSeen(); setShowNotifications(false); }}>Close</button>
          </div>

          <div style={notificationBody}>
            {notifications.deposits.map((item) => {
              const userLabel = item.user_name && item.user_phone
                ? `${item.user_name} • ${item.user_phone}`
                : item.user_name || item.user_phone || `User ${item.user_id}`;
              const dateLabel = item.created_at ? new Date(item.created_at).toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

              return (
                <button key={`d-${item.id}`} type="button" style={notificationItem} onClick={() => openNoticePage("/admin/deposits")}> 
                  <div>
                    <strong style={noticeTitle}>Approved Deposit</strong>
                    <div style={noticeSub}>{userLabel}</div>
                    <small style={noticeStatus}>{`${dateLabel} • approved`}</small>
                  </div>
                  <strong style={noticeAmount}>{`Rs ${Number(item.amount || 0).toFixed(0)}`}</strong>
                </button>
              );
            })}

            {notifications.withdraws.map((item) => {
              const userLabel = item.user_name && item.user_phone
                ? `${item.user_name} • ${item.user_phone}`
                : item.user_name || item.user_phone || `User ${item.user_id}`;
              const dateLabel = item.created_at ? new Date(item.created_at).toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

              return (
                <button key={`w-${item.id}`} type="button" style={notificationItem} onClick={() => openNoticePage("/admin/payouts")}> 
                  <div>
                    <strong style={noticeTitle}>Approved Withdraw</strong>
                    <div style={noticeSub}>{userLabel}</div>
                    <small style={noticeStatus}>{`${dateLabel} • approved`}</small>
                  </div>
                  <strong style={{ ...noticeAmount, color: "#dc2626" }}>{`Rs ${Number(item.amount || 0).toFixed(0)}`}</strong>
                </button>
              );
            })}

            {!visibleNoticeCount && <div style={emptyNotice}>No approved deposit or payout entries</div>}
          </div>

          <div style={notificationFooter}>
            <button type="button" style={footerLinkLeft} onClick={() => {
              markNotificationsSeen();
              setShowNotifications(false);
            }}>Mark all as read</button>
            <button type="button" style={footerLinkRight} onClick={() => openNoticePage("/admin/deposits")}>View All</button>
          </div>
        </div>
      )}
      {showLeaderboard && (
        <div style={leaderboardBackdrop} onClick={() => setShowLeaderboard(false)}>
          <div style={leaderboardCard} onClick={(event) => event.stopPropagation()}>
            <div style={notificationHeader}>
              <div>
                <strong>Top 5 Admin Shift Time</strong>
                <div style={notificationSub}>Live 30 day ranking by active time</div>
              </div>
              <button type="button" style={markReadButton} onClick={() => setShowLeaderboard(false)}>Close</button>
            </div>
            <div style={leaderboardBody}>
              {activity.slice(0, 5).map((item) => (
                <div key={`${item.actor_type}-${item.actor_id}`} style={leaderboardRow}>
                  <span style={leaderboardRank}>#{item.rank}</span>
                  <div style={{ minWidth: 0 }}>
                    <strong style={leaderboardName}>{item.name || "Admin"}</strong>
                    <div style={leaderboardMeta}>{item.email || item.actor_type} · {formatDateTime(item.last_seen_at)}</div>
                  </div>
                  <strong style={leaderboardTime}>{formatDuration(item.active_seconds)}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function formatDuration(secondsValue) {
  const seconds = Number(secondsValue || 0);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatDateTime(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function clampZoom(value) {
  const parsed = Number.parseFloat(value);
  const safe = Number.isFinite(parsed) ? parsed : 1;
  return Number(Math.min(1.05, Math.max(0.75, safe)).toFixed(2));
}

function getSeenNoticeKey(admin) {
  return `admin_seen_dw_notice_${admin?.role || "admin"}_${admin?.id || "main"}`;
}

function readSeenNotice(admin) {
  try {
    const parsed = JSON.parse(localStorage.getItem(getSeenNoticeKey(admin)) || "{}");
    return {
      depositIds: normalizeIdList(parsed.depositIds),
      withdrawIds: normalizeIdList(parsed.withdrawIds),
      deposits: Number(parsed.deposits || 0),
      withdraws: Number(parsed.withdraws || 0),
    };
  } catch {
    return { depositIds: [], withdrawIds: [], deposits: 0, withdraws: 0 };
  }
}

function normalizeIdList(value) {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(value.map((item) => String(item || "").trim()).filter(Boolean))).slice(0, 80);
}

function getMaxId(items = []) {
  return items.reduce((max, item) => Math.max(max, Number(item?.id || 0)), 0);
}

function mergeSeenIds(existing = [], items = []) {
  return normalizeIdList([...existing, ...items.map((item) => item?.id)]);
}

function sameIdList(left = [], right = []) {
  return normalizeIdList(left).join("|") === normalizeIdList(right).join("|");
}

function getUnseenItems(items = [], seenNotice = {}, type = "deposits") {
  const ids = new Set(normalizeIdList(type === "withdraws" ? seenNotice.withdrawIds : seenNotice.depositIds));
  const safeSeen = Number(type === "withdraws" ? seenNotice.withdraws || 0 : seenNotice.deposits || 0);
  return items.filter((item) => {
    const id = String(item?.id || "").trim();
    if (!id) return false;
    if (ids.size > 0) return !ids.has(id);
    return Number(item?.id || 0) > safeSeen;
  });
}

function getUnseenNoticeCount(notifications, seenNotice) {
  return getUnseenItems(notifications.deposits, seenNotice, "deposits").length
    + getUnseenItems(notifications.withdraws, seenNotice, "withdraws").length;
}

const topbar = {
  height: "74px",
  background: "#fffaf0",
  borderBottom: "1px solid #fde68a",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 22px",
  boxSizing: "border-box",
  position: "sticky",
  top: 0,
  zIndex: 10
};

const darkTopbar = (base) => ({
  ...base,
  background: "#020617",
  borderBottom: "1px solid #1e293b",
  color: "#e5e7eb",
  boxShadow: "0 16px 36px rgba(2,6,23,.35)"
});

const laptopTopbar = {
  ...topbar,
  height: "64px",
  padding: "0 12px"
};

const mobileTopbar = {
  ...topbar,
  minHeight: "56px",
  height: "auto",
  padding: "calc(env(safe-area-inset-top) + 8px) 8px 8px",
  gap: "8px"
};

const leftCluster = {
  display: "flex",
  alignItems: "center",
  gap: "14px"
};

const laptopLeftCluster = {
  ...leftCluster,
  gap: "9px"
};

const mobileLeftCluster = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  minWidth: 0
};

const mobileMenuButton = {
  width: "36px",
  height: "36px",
  border: "1px solid #fde68a",
  background: "#fff",
  color: "#92400e",
  borderRadius: "12px",
  display: "grid",
  placeItems: "center",
  flexShrink: 0
};

const userPill = {
  height: "54px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "0 16px",
  border: "1px solid #bbf7d0",
  background: "#ecfdf5",
  color: "#047857",
  fontSize: "14px",
  borderRadius: "14px"
};

const laptopUserPill = {
  ...userPill,
  height: "46px",
  gap: "9px",
  padding: "0 12px",
  fontSize: "13px",
  borderRadius: "12px"
};

const mobileUserPill = {
  ...userPill,
  height: "36px",
  gap: "8px",
  padding: "0 8px",
  fontSize: "12px",
  borderRadius: "12px",
  maxWidth: "min(28vw, 124px)",
  overflow: "hidden"
};

const avatar = {
  width: "34px",
  height: "34px",
  display: "grid",
  placeItems: "center",
  background: "#10b981",
  color: "#fff",
  fontWeight: 800,
  borderRadius: "10px"
};

const rightCluster = {
  display: "flex",
  alignItems: "center",
  gap: "12px"
};

const laptopRightCluster = {
  ...rightCluster,
  gap: "8px"
};

const mobileRightCluster = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  flexShrink: 0
};

const timePill = {
  height: "42px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "0 16px",
  border: "1px solid #fde68a",
  background: "#fffbeb",
  color: "#92400e",
  fontSize: "14px",
  borderRadius: "999px",
  fontWeight: 700
};

const laptopTimePill = {
  ...timePill,
  height: "38px",
  padding: "0 12px",
  fontSize: "13px"
};

const iconButton = {
  width: "46px",
  height: "46px",
  display: "grid",
  placeItems: "center",
  border: "1px solid #f3e8c8",
  background: "#fff",
  color: "#8b5e34",
  borderRadius: "18px",
  position: "relative"
};
const laptopIconButton = { ...iconButton, width: "40px", height: "40px", borderRadius: "13px" };
const mobileIconButton = { ...iconButton, width: "34px", height: "34px", borderRadius: "11px" };
const zoomTrigger = { ...iconButton, width: "62px", fontSize: "12px", fontWeight: 900, color: "#1d4ed8", background: "#eff6ff", borderColor: "#bfdbfe" };
const laptopZoomTrigger = { ...zoomTrigger, width: "52px", height: "44px", borderRadius: "14px", fontSize: "11px" };
const mobileZoomTrigger = { ...zoomTrigger, width: "46px", height: "34px", borderRadius: "11px", fontSize: "10px" };
const themeButton = { ...iconButton, width: "82px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "7px", fontSize: "12px", fontWeight: 900 };
const laptopThemeButton = { ...themeButton, width: "70px", height: "44px", borderRadius: "14px", fontSize: "11px" };
const mobileThemeButton = { ...themeButton, width: "58px", height: "34px", borderRadius: "11px", gap: "4px", fontSize: "10px" };
const toolWrap = { position: "relative", display: "inline-flex" };
const zoomPanel = { position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 40, minWidth: "162px", height: "48px", border: "1px solid #dbeafe", borderRadius: "16px", background: "#fff", boxShadow: "0 18px 42px rgba(15,23,42,.16)", display: "grid", gridTemplateColumns: "44px 1fr 44px", alignItems: "center", gap: "5px", padding: "4px" };
const mobileZoomPanel = { ...zoomPanel, position: "fixed", top: "calc(env(safe-area-inset-top) + 56px)", right: "54px" };
const zoomToolButton = { height: "40px", border: 0, borderRadius: "12px", background: "#eff6ff", color: "#1d4ed8", display: "grid", placeItems: "center", cursor: "pointer" };
const zoomValue = { textAlign: "center", color: "#0f172a", fontSize: "12px", fontWeight: 900 };
const noticeCount = { position: "absolute", top: "4px", right: "6px", minWidth: "22px", height: "22px", padding: "0 6px", borderRadius: "999px", background: "#ef4444", color: "#fff", fontSize: "11px", fontWeight: 800, display: "grid", placeItems: "center" };

const logoutButton = {
  ...iconButton,
  borderColor: "#fecaca",
  color: "#dc2626"
};
const laptopLogoutButton = { ...laptopIconButton, borderColor: "#fecaca", color: "#dc2626" };
const mobileLogoutButton = { ...mobileIconButton, borderColor: "#fecaca", color: "#dc2626" };

const nameEditorPanel = { position: "absolute", top: "74px", left: "22px", zIndex: 45, width: "270px", border: "1px solid #bbf7d0", borderRadius: "18px", background: "#fff", boxShadow: "0 24px 56px rgba(15,23,42,.18)", padding: "14px", display: "grid", gap: "10px" };
const mobileNameEditorPanel = { ...nameEditorPanel, position: "fixed", top: "calc(env(safe-area-inset-top) + 58px)", left: "10px", right: "10px", width: "auto" };
const nameEditorTitle = { color: "#047857", fontSize: "13px", fontWeight: 900 };
const nameEditorInput = { height: "42px", border: "1px solid #bbf7d0", borderRadius: "12px", padding: "0 12px", outline: 0, color: "#0f172a", fontWeight: 800 };
const nameEditorSave = { height: "40px", border: 0, borderRadius: "12px", background: "#10b981", color: "#fff", fontWeight: 900, cursor: "pointer" };
const notificationPanel = { position: "absolute", top: "74px", right: "116px", width: "380px", maxHeight: "540px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "22px", boxShadow: "0 28px 60px rgba(15,23,42,0.16)", overflow: "hidden", zIndex: 20 };
const mobileNotificationPanel = { ...notificationPanel, position: "fixed", top: "calc(env(safe-area-inset-top) + 68px)", left: "10px", right: "10px", width: "auto", maxHeight: "calc(100vh - env(safe-area-inset-top) - 84px)", borderRadius: "16px", zIndex: 30 };
const notificationHeader = { padding: "18px 18px 14px", borderBottom: "1px solid #eef2f7", display: "flex", alignItems: "center", justifyContent: "space-between" };
const notificationSub = { marginTop: "4px", color: "#64748b", fontSize: "12px" };
const markReadButton = { border: 0, background: "transparent", color: "#2563eb", fontWeight: 700, cursor: "pointer" };
const notificationBody = { maxHeight: "400px", overflowY: "auto" };
const notificationItem = { width: "100%", border: 0, background: "#fff", padding: "16px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f1f5f9", cursor: "pointer", textAlign: "left" };
const noticeTitle = { display: "block", color: "#16a34a", fontSize: "15px" };
const noticeSub = { marginTop: "6px", color: "#475569", fontSize: "12px" };
const noticeStatus = { marginTop: "4px", display: "block", color: "#64748b", fontSize: "11px", textTransform: "capitalize" };
const noticeAmount = { color: "#16a34a", fontSize: "16px" };
const emptyNotice = { padding: "24px", textAlign: "center", color: "#94a3b8" };
const notificationFooter = { display: "grid", gridTemplateColumns: "1fr 1fr", borderTop: "1px solid #eef2f7" };
const footerLinkLeft = { height: "48px", border: 0, background: "#fff7ed", color: "#ea580c", fontWeight: 700, cursor: "pointer" };
const footerLinkRight = { height: "48px", border: 0, background: "#f0fdf4", color: "#16a34a", fontWeight: 700, cursor: "pointer" };
const leaderboardBackdrop = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.42)", display: "grid", placeItems: "center", zIndex: 60 };
const leaderboardCard = { width: "min(560px, 92vw)", background: "#fff", borderRadius: "22px", border: "1px solid #e5e7eb", boxShadow: "0 24px 56px rgba(15,23,42,0.16)", overflow: "hidden" };
const leaderboardBody = { display: "grid", gap: 0 };
const leaderboardRow = { display: "grid", gridTemplateColumns: "48px 1fr auto", gap: "12px", alignItems: "center", padding: "14px 18px", borderTop: "1px solid #f1f5f9" };
const leaderboardRank = { width: "34px", height: "34px", borderRadius: "999px", background: "#eff6ff", color: "#2563eb", display: "grid", placeItems: "center", fontSize: "12px", fontWeight: 900 };
const leaderboardName = { display: "block", color: "#0f172a", fontSize: "14px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const leaderboardMeta = { marginTop: "4px", color: "#64748b", fontSize: "12px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const leaderboardTime = { color: "#7c3aed", fontSize: "13px", fontWeight: 900 };

export default AdminTopbar;
